// https://itnext.io/creating-reusable-abstractions-with-amplify-and-react-hooks-97784c8b5c2a

import jwt_decode from "jwt-decode";
import React, {useState} from "react";
import {Amplify} from "aws-amplify";
import {fetchAuthSession, getCurrentUser, signOut, signInWithRedirect, signIn} from "aws-amplify/auth";
import {get} from "aws-amplify/api";

//@todo build system must swap out dev for prod configs
import awsconfig from "../configs/awsconfig.json";
import awsauth from "../configs/awsauth.json";

export interface IAuthUser {
  isLoading: boolean;
  identityId: string;
  email: string;
  authenticated: boolean;
  jwtToken: string;
  settings: IUserSettings;
}

export const DEFAULT_USER_AUTH: IAuthUser = Object.freeze({
  isLoading: true,
  identityId: "",
  email: "",
  authenticated: false,
  jwtToken: "",
  settings: {
    // multiply javascripts timezone offset by -1
    // becuase all moment function calls need a timezone
    // in hours & minutes negative if behind UTC and positive
    // if ahead of UTC, which is the opposite of how JS's
    // timezone offset works.
    timezoneOffsetMinutes: -1 * new Date().getTimezoneOffset(),
    firstName: "",
    lastName: ""
  }
});

export interface IUserSettings {
  timezoneOffsetMinutes: number;
  firstName: string;
  lastName: string;
}

interface IAuthContextInterface {
  user: IAuthUser;
  login: () => void;
  loginWithHostedUI: () => void;
  customSignIn: (email: string, password: string) => Promise<any>;
  logout: () => Promise<void>;
  refreshJwt: (user: IAuthUser) => void;
  isInitialized: boolean;
  fetchUser: () => Promise<void>;
}

export const AuthContext = React.createContext<IAuthContextInterface>({
  user: DEFAULT_USER_AUTH,
  login: () => {
    // Default implementation
  },
  loginWithHostedUI: () => {
    // Default implementation
  },
  customSignIn: async () => Promise.resolve({}),
  logout: async () => {
    // Default implementation
  },
  refreshJwt: () => {
    // Default implementation
  },
  isInitialized: false,
  fetchUser: async () => {
    // Default implementation
  }
});

// Configure Amplify v6
// For localhost development, use the current location as redirect URLs
const isLocalhost = window.location.hostname === "localhost";
const redirectSignIn = isLocalhost ? `${window.location.origin}/upload` : awsauth.redirectSignIn;
const redirectSignOut = isLocalhost ? window.location.origin : awsauth.redirectSignOut;

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: awsconfig.Auth.userPoolId,
      userPoolClientId: awsconfig.Auth.userPoolWebClientId,
      loginWith: {
        oauth: {
          domain: awsauth.domain,
          scopes: awsauth.scope,
          redirectSignIn: [redirectSignIn],
          redirectSignOut: [redirectSignOut],
          responseType: awsauth.responseType as "code" | "token"
        }
      }
    }
  },
  API: {
    REST: {
      app: {
        endpoint: awsconfig.API.endpoints.find((e: any) => e.name === "app")?.endpoint || "",
        region: awsconfig.Auth.region
      },
      data: {
        endpoint: awsconfig.API.endpoints.find((e: any) => e.name === "data")?.endpoint || "",
        region: awsconfig.Auth.region
      }
    }
  }
});

export const AuthProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [user, setUser] = React.useState<IAuthUser>(DEFAULT_USER_AUTH);
  const [isInitialized, setIsInitialized] = useState(false);

  const login = () => signInWithRedirect();
  const loginWithHostedUI = () => signInWithRedirect();

  const customSignIn = async (email: string, password: string) => {
    // Dual-attempt sign-in strategy:
    // 1. Try with exact email as entered (supports existing mixed-case users)
    // 2. If UserNotFoundException, try lowercase (supports new lowercase-only users)
    let result = null;

    try {
      result = await signIn({
        username: email,
        password
      });
    } catch (firstError: any) {
      // If user not found and email contains uppercase, try lowercase
      if (firstError.name === "UserNotFoundException" && email !== email.toLowerCase()) {
        result = await signIn({
          username: email.toLowerCase(),
          password
        });
      } else {
        throw firstError;
      }
    }

    // After successful sign-in, fetch the user
    if (result && result.isSignedIn) {
      await fetchUser();
    }

    return result;
  };

  const logout = async () => {
    await signOut();
    window.location.href = awsauth.redirectSignOut;
  };

  const refreshJwt = async (user: IAuthUser) => {
    if (user && user.authenticated) {
      const jwtToken = await _getJwtToken();
      if (jwtToken) {
        user.jwtToken = jwtToken;
        setUser(user);
      }
    }
  };

  const _getJwtToken = async (): Promise<string | undefined> => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      return idToken?.toString();
    } catch {
      //console.log("Unable to get JWT", error);
      return undefined;
    }
  };

  const _fetchAuthUser = async (): Promise<IAuthUser> => {
    let authUser: IAuthUser = DEFAULT_USER_AUTH;
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      const idToken = session.tokens?.idToken;
      // Use the user's sub/userId as identityId since we don't have federated identity
      const identityId = currentUser.userId;

      if (idToken && identityId) {
        const decodedJWT: any = jwt_decode(idToken.toString());
        authUser = {
          isLoading: false,
          identityId: identityId,
          email: decodedJWT.email || "",
          authenticated: true,
          jwtToken: idToken.toString(),
          settings: DEFAULT_USER_AUTH.settings
        };
      }
    } catch {
      //console.log("_fetchAuthUser error", error);
    }
    return authUser;
  };

  const fetchUser = async () => {
    let user = null;
    try {
      user = await getCurrentUser();
    } catch {
      // If user is not logged in then he'll
      // get an error fetching so set user to default
      user = DEFAULT_USER_AUTH;
    }

    if (user && (user as any).userId) {
      const authUser = await _fetchAuthUser();

      // if there is JWT token then get the user settings from API call
      if (authUser.jwtToken) {
        try {
          const session = await fetchAuthSession();
          const idToken = session.tokens?.idToken;
          const params = {
            headers: {
              Authorization: idToken?.toString() || ""
            }
          };
          const {body} = await get({
            apiName: "app",
            path: `/settings/${authUser.identityId}`,
            options: params
          }).response;
          const settings = await body.json();
          authUser.settings = settings as unknown as IUserSettings;
        } catch {
          //console.log("Error getting settings", error);
        }
      }

      setUser(authUser);
    } else {
      setUser({...DEFAULT_USER_AUTH, isLoading: false});
    }
    setIsInitialized(true);
  };

  React.useEffect(() => {
    fetchUser();
  }, []);

  const state: IAuthContextInterface = {
    user,
    login,
    loginWithHostedUI,
    customSignIn,
    logout,
    refreshJwt,
    isInitialized,
    fetchUser
  };

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
};

export const useUser = () => React.useContext(AuthContext);
